import React, { useEffect, useMemo, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import cn from "classnames";
import { debounce } from "lodash";

import { InputField } from "components/form/InputField";
import { TickCircle } from "components/icons/components/TickCircle";
import { RadioButtonField } from "components/form/RadioButtonField";
import { ToggleText } from "components/ToggleText";

import "./EDDMCampaignTrackingCard.css";
import { updateCampaign, updateCampaignExtraData } from "../../../../../graphQL";

interface EDDMCampaignTrackingCardProps {
  campaignId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isReadOnly?: boolean;
  isFileUploaded?: boolean;
  hasQRCode: "yes" | "no" | "";
  setHasQRCode: (value: "yes" | "no" | "") => void;
  qrCodeUrl: string;
  setQrCodeUrl: (value: string) => void;
  additionalComment: string;
  setAdditionalComment: (value: string) => void;
  setIsSaving: (isSaving: boolean) => void;
  designHtmlContent?: string;
}

export const EDDMCampaignTrackingCard = ({
  campaignId,
  isOpen,
  setIsOpen,
  isReadOnly,
  isFileUploaded,
  hasQRCode,
  setHasQRCode,
  qrCodeUrl,
  setQrCodeUrl,
  additionalComment,
  setAdditionalComment,
  setIsSaving,
  designHtmlContent,
}: EDDMCampaignTrackingCardProps) => {
  // Detect if QR code exists in the design
  const hasQRInDesign = useMemo(() => {
    if (!designHtmlContent) return false;
    return designHtmlContent.includes("data-qr-code");
  }, [designHtmlContent]);

  // Check if design was uploaded (not from design tool)
  const isDesignUploaded = !designHtmlContent;

  // Show QR question if design is uploaded OR if QR is detected in design
  const shouldShowQRCodeQuestion = isDesignUploaded || hasQRInDesign;

  // Check if form is valid
  const isValid = useMemo(() => {
    if (!shouldShowQRCodeQuestion) return true;
    if (!hasQRCode) return false;
    if (hasQRCode === "yes" && !qrCodeUrl) return false;
    return true;
  }, [shouldShowQRCodeQuestion, hasQRCode, qrCodeUrl]);

  // Auto-save QR code URL and coupon code
  // We use "none" as a marker to indicate user explicitly selected "No"
  // This distinguishes from null/undefined which means "never selected"
  const handleQRCodeUpdate = useCallback(async () => {
    let qrCodeLinkValue: string | null = null;

    if (hasQRCode === "no") {
      // User explicitly selected "No" - save marker value
      qrCodeLinkValue = null;
    } else if (hasQRCode === "yes" && qrCodeUrl?.length) {
      // User selected "Yes" with URL
      qrCodeLinkValue =
        qrCodeUrl?.startsWith("https://") || qrCodeUrl?.startsWith("http://") ? qrCodeUrl : `https://${qrCodeUrl}`;
    }

    await updateCampaignExtraData({
      campaignId,
      qrCodeLink: qrCodeLinkValue,
      isTrackableCoupon: 0,
      couponType: "static",
    });
  }, [campaignId, hasQRCode, qrCodeUrl]);

  // Auto-save additional comment
  const handleAdditionalCommentUpdate = useCallback(async () => {
    await updateCampaign({
      campaignId,
      comments: additionalComment || null,
    });
  }, [campaignId, additionalComment]);

  // Debounced auto-save
  const autoSaveFields = useMemo(
    () =>
      debounce(async () => {
        if (isReadOnly) return;
        setIsSaving(true);
        await Promise.allSettled([handleQRCodeUpdate(), handleAdditionalCommentUpdate()]);
        setIsSaving(false);
      }, 1000),
    [isReadOnly, handleQRCodeUpdate, handleAdditionalCommentUpdate, setIsSaving]
  );

  useEffect(() => {
    autoSaveFields();
    return () => {
      autoSaveFields.cancel();
    };
  }, [hasQRCode, qrCodeUrl, additionalComment]);

  // Pre-select QR code if detected in design
  useEffect(() => {
    if (hasQRInDesign && !hasQRCode) {
      setHasQRCode("yes");
    }
  }, [hasQRInDesign, hasQRCode, setHasQRCode]);

  return (
    <div
      className={cn(
        "bg-white dark:bg-card rounded-lg shadow-[0px_2px_6px_0px_rgba(39,55,84,0.08)] dark:shadow-none border border-gray-100 dark:border-border w-full p-8",
        !isOpen && "eddmTrackingCardClosed"
      )}
    >
      <div className="space-y-5">
        {/* Header - matches DesignSelection */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="space-y-0.5 max-w-lg">
              <h2 className="text-blue-deep-dark dark:text-foreground text-lg font-semibold leading-tight">
                Campaign tracking
              </h2>
            </div>
            {isValid && isFileUploaded && <TickCircle />}
          </div>

          <div
            className="seeMoreToggle"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            style={{ cursor: "pointer", zIndex: 10 }}
            data-cypress-id={"seeMoreLessToggle"}
          >
            <ToggleText>See {isOpen ? "less" : "more"}</ToggleText>
            <ChevronDown className={cn("toggleIcon h-5 w-5", isOpen && "toggleIconOpen")} />
          </div>
        </div>

        {/* Content */}
        <div className={cn("eddmTrackingCardContent", !isOpen && "eddmTrackingCardContentClosed")}>
          {shouldShowQRCodeQuestion && (
            <>
              {hasQRInDesign && (
                <div className="qrDetectedBanner">
                  <p className="text-[13px] font-medium text-[#2e7d32] dark:text-success-foreground">
                    QR code detected in your design
                  </p>
                </div>
              )}
              <RadioButtonField
                disabled={isReadOnly}
                label="Would you like to track a QR code for your campaign?"
                name="hasQRCode"
                value={hasQRCode}
                onChange={(value) => {
                  setHasQRCode(value as "yes" | "no");
                  if (value === "no") {
                    setQrCodeUrl("");
                  }
                }}
                options={[
                  { label: "Yes", value: "yes" },
                  { label: "No", value: "no" },
                ]}
                required
              />

              {hasQRCode === "yes" && (
                <InputField
                  disabled={isReadOnly}
                  label="What is the QR code URL?"
                  name="qrCodeUrl"
                  placeholder="e.g., https://www.mybusiness.com"
                  value={qrCodeUrl}
                  onChange={(e) => setQrCodeUrl(e.target.value)}
                  required
                />
              )}
            </>
          )}

          <div>
            <InputField
              disabled={isReadOnly}
              label="Share any additional comment"
              name="additionalComment"
              placeholder=""
              value={additionalComment}
              onChange={(e) => setAdditionalComment(e.target.value)}
              multiline
              rows={6}
              inputProps={{ maxLength: 240 }}
            />
            <p className="text-grey-medium text-xs mt-1" data-cypress-id={"additionalCommentLength"}>
              {additionalComment?.length ?? 0} of 240 characters
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
